import {
  MathUtils,
  Vector3,
  Mesh,
  ShaderMaterial,
  AdditiveBlending,
  DoubleSide,
  CylinderGeometry,
  CanvasTexture,
  NoBlending,
  CircleGeometry,
  Sprite,           
  SpriteMaterial, 
  LinearFilter  
} from "three";

import { createJets, updateJets } from "../utils/jets.js";

import distortionDiskVertex from "../../../shaders/diskDistortion/vertex.glsl";
import distortionDiskFragment from "../../../shaders/diskDistortion/fragment.glsl";
import diskVertex from "../../../shaders/disk/vertex.glsl";
import diskFragment from "../../../shaders/disk/fragment.glsl";
import { CSS2DObject } from "three/examples/jsm/renderers/CSS2DRenderer";

import { INCLIN_GALAXY, WAVE_SPEED } from "../configEntities/galaxyConfig.js";

import { attrNebSarg } from "../configEntities/groupsAttr.js";

import {
  createConcentricMagneticLines,
  createWavyColumnLines,
  updateColumnLines,
  updateMagneticLines,
} from "../utils/magneticHelicalDisk.js";

import { showInfoPanel } from "../ui.js";
import { attrSagittarius } from '../configEntities/groupsAttr.js';

import { sceneManager} from "../script";
import { bodies } from "../configEntities/celestialDynamicsConfig.js";
import { normalizeAlphabetic } from "../helpers.js";

import { orbit } from "../utils/orbitUtils.js";
import { PulsarNebula } from "../groupsEntities/windNebula.js";

import { cameraLabelClick } from "../utils/cameraUtils.js";
import { Galaxy } from "../groupsEntities/galaxy.js";
import { Nebula } from "../groupsEntities/nebula.js";

export class BlackHole {
  constructor(size, label) {
    const distSargitarius = 26640 * 63241.1 * 23.4117;
    const z = distSargitarius * Math.cos(INCLIN_GALAXY);
    const y = -distSargitarius * Math.sin(INCLIN_GALAXY);
    const posHoleBlack = new Vector3(0, y, z);

    this.distSpace = 1
    this.scaleSpace = 1

    const n = new Vector3(0, Math.sin(INCLIN_GALAXY), Math.cos(INCLIN_GALAXY))
    let d = n.dot(posHoleBlack)
    this.posHW = posHoleBlack.clone().sub(n.multiplyScalar(1.5 * d));




    this.scene = sceneManager.scene;
    this.camera = sceneManager.camera;
    this.pos = posHoleBlack;
    this.size = size ;
    this.time = 0
    this.oscMag = 0
    this.precessionPhase = 0
    this.warpPhase = 0

    this.name = label;
    this.distSargitarius = 26490 * 63241.1 * 23.4117;
    this.speedTrans = -9.016846e-11;
    this.objOsc = bodies[normalizeAlphabetic(this.name)];

    this.distScale = this.objOsc.distScale;
    this.axialTilt = 180;
    this.distRot = this.objOsc.distRot;
    
    this.createGradient();
    this.createMesh();
    this.createDistortion();
    this.posLabel = new Vector3();

    this.jets = createJets(this,400, attrSagittarius, attrSagittarius.jetConfig, false)

    this.mesh.position.add(this.innerMesh);
    this.mesh.position.add(this.meshDisk);

    this.parts = [
    new Galaxy(this),
    new Nebula(this),
    new PulsarNebula(600, this, {maxRadius: 12 }, attrNebSarg)
    ];
    
  }
  createGradient() {
    this.gradient = {};
    this.gradient.canvas = document.createElement("canvas");
    this.gradient.canvas.width = 1;
    this.gradient.canvas.height = 128;
    this.gradient.context = this.gradient.canvas.getContext("2d");
    this.gradient.style = this.gradient.context.createLinearGradient(
      0,
      0,
      0,
      this.gradient.canvas.height
    );

    this.gradient.style.addColorStop(0.0, "#FFFFBB");
    this.gradient.style.addColorStop(0.15, "#FFCC66");
    this.gradient.style.addColorStop(0.35, "#FF9933");
    this.gradient.style.addColorStop(0.55, "#FF6633");
    this.gradient.style.addColorStop(0.75, "#CC3300");
    this.gradient.style.addColorStop(0.9, "#660000");
    this.gradient.style.addColorStop(1.0, "#000000");

    this.gradient.context.fillStyle = this.gradient.style;
    this.gradient.context.fillRect(
      0,
      0,
      this.gradient.canvas.width,
      this.gradient.canvas.height
    );
    this.gradient.texture = new CanvasTexture(this.gradient.canvas);
  }

  createMesh() {
    this.geometry = new CylinderGeometry(0.1, 12.6, 0, 64, 8, true);
    this.material = new ShaderMaterial({
      side: DoubleSide,
      vertexShader: distortionDiskVertex,
      fragmentShader: distortionDiskFragment,
      uniforms: {
        uTime: { value: 0 },
        uGradientTexture: { value: this.gradient.texture },
        uNoisesTexture: { value: sceneManager.noises.renderTarget.texture },
        uBeamingAngle: { value: 0 },
        uBeamingStrength: { value: 2.5 },
        uBeamingFactor: { value: 1 },
      },
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: true,
      depthTest: true,
    });

    this.mesh = new Mesh(this.geometry, this.material);
    this.mesh.position.set(this.pos.x, this.pos.y, this.pos.z);
    this.scene.add(this.mesh);

    const spriteSize = 0.05;
    const res = 128;
    const circleCanvas = document.createElement("canvas");
    circleCanvas.width = res;
    circleCanvas.height = res;
    const ctx = circleCanvas.getContext("2d");

    ctx.clearRect(0, 0, res, res);

    ctx.fillStyle = "#000000";
    ctx.beginPath();
    ctx.arc(res / 2, res / 2, res / 2, 0, Math.PI * 2);
    ctx.fill();

    const circleTexture = new CanvasTexture(circleCanvas);
    circleTexture.minFilter = LinearFilter;
    circleTexture.magFilter = LinearFilter;

    this.innerMesh = new Sprite(
      new SpriteMaterial({
        map: circleTexture,
        transparent: true,
        alphaTest: 0.5,
      })
    );

    this.innerMesh.scale.set(spriteSize, spriteSize, 1);
    this.innerMesh.position.set(this.pos.x, this.pos.y, this.pos.z);
    this.scene.add(this.innerMesh);

    this.diskLines = createConcentricMagneticLines(80, 20, 2, 10, this.mesh);
    this.upLines = createWavyColumnLines(
      22,
      40,
      40,
      0.25,
      10,
      6,
      true,
      this.mesh
    );
    this.downLines = createWavyColumnLines(
      22,
      40,
      40,
      0.25,
      10,
      6,
      false,
      this.mesh
    );

    let labeNorm = normalizeAlphabetic(this.name);

    this.div = document.createElement("div");
    this.div.className = "label";
    this.div.textContent = this.name;
    this.div.style.backgroundColor = "transparent";
    this.div.style.cursor = "pointer";
    this.infoIcon = document.createElement("i");
    this.infoIcon.className = "fas fa-info-circle info-icon";
    this.infoIcon.style.pointerEvents = "auto";
    this.infoIcon.title = "More Information";
    this.div.appendChild(this.infoIcon);
    this.div.addEventListener("click", () => {
      cameraLabelClick(labeNorm, this);
    });

    this.infoIcon.addEventListener("click", (evt) => {
      evt.stopPropagation();
      showInfoPanel(this.name);
    });

    const canvas = sceneManager.renderer.domElement;
    this.div.addEventListener('wheel', e => {
      e.preventDefault();        
      const evt = new WheelEvent('wheel', e);
      canvas.dispatchEvent(evt);    
    });

    this.label = new CSS2DObject(this.div);
    this.label.position.set(0, this.size * 1.1, 0);
    this.label.center.set(0, 1);
    this.mesh.add(this.label);
    this.label.layers.set(0);
  }

  createDistortion() {
    const angle1 = Math.atan2(this.camera.position.x, this.camera.position.z);
    const angleDeg = -MathUtils.radToDeg(angle1);
    this.geometryDisk = new CircleGeometry(168.8);
    this.materialDisk = new ShaderMaterial({
      side: DoubleSide,
      vertexShader: diskVertex,
      fragmentShader: diskFragment,
      blending: NoBlending,
      depthTest: false,
      depthWrite: true,
      uniforms: { angle: { value: angleDeg } }
    });
    this.meshDisk = new Mesh(
      this.geometryDisk,
      this.materialDisk
    );
    this.meshDisk.rotation.x = Math.PI * 0.5;
    sceneManager.distortion.scene.add(this.meshDisk);

  }
  applyGrav(grav) {
    const distanceHole = sceneManager.camera.position.distanceTo(this.pos);

    if (distanceHole < 1.3e7) {
      const attractionForce = Math.min(10, grav / distanceHole);
      const attractionDirection = new Vector3()
        .copy(this.pos)
        .sub(sceneManager.camera.position)
        .normalize();
      sceneManager.camera.position.add(attractionDirection.multiplyScalar(attractionForce));
      if (distanceHole < 2.0) {
        sceneManager.camera.position.copy(this.posHW).add(new Vector3(0.2, 0.2, 0.2));
        sceneManager.dimension = !sceneManager.dimension
        sceneManager.composition.plane.material.uniforms.uColorChange.value = sceneManager.dimension
        
      }
    }
  }
  calculateBeaming(liMin) {
    const camDir = new Vector3()
      .subVectors(sceneManager.camera.position, this.pos)
      .normalize();
    const dot = Math.abs(camDir.dot(new Vector3(0, 1, 0)));
    const angleOpacity = MathUtils.clamp(1 - dot, 0, 1.2);
    const dNorm = MathUtils.clamp(
      sceneManager.camera.position.distanceTo(this.pos) / 8,
      0,
      1.25
    );
    const proximityOpacity = Math.max(liMin, dNorm);
    const effect = MathUtils.clamp(angleOpacity * proximityOpacity, 0,1.25);
    return effect;
  }
  updateParts(speed) {
  this.parts.forEach(group => group.animate(speed));
  }
  animate(speed) {
   sceneManager.camera.updateMatrixWorld()
    const radius = this.distSargitarius;
    const angle = 0;
    const eccentricity = 0;
    const argPeriapsis = 0;
    const longAscNode = 0;
    const precession = 0;
    const warp = true;
    this.label.visible = sceneManager.commands.controls.labels;
    const speedDisk = Math.abs(speed*5e3);

    this.material.uniforms.uTime.value -= Math.sign(speed)*Math.min(
      speedDisk,
      8e-2
    );

    this.time = ((this.time+speed*this.speedTrans)% (2 * Math.PI))
    const speedMag = Math.abs(speed*6e3)
    this.oscMag += Math.sign(speed)*Math.min(26,speedMag)
    this.precessionPhase =
    (this.precessionPhase + speed * precession) % (2 * Math.PI);

    this.warpPhase = (this.warpPhase + speed * WAVE_SPEED) % (2 * Math.PI);

    this.applyRotateDisk()
    
    const { x0, y0, z0 } = orbit({
     radius:        radius,
     angle:         angle,
     eccentricity:  eccentricity,
     time:          this.time,
     center:        {x:0, y:0, z:0},
     inclinationOsc:{ amplitude: bodies.sun.ia, frequency: bodies.sun.ifreq },
     radiusOsc:     { amplitude: bodies.sun.ra, frequency: bodies.sun.rfreq },
     orientation: {
      argPeriapsis:    argPeriapsis,
      longAscNode:     longAscNode,
      precessionPhase: this.precessionPhase,
   },
   warp: {
    enabled: warp,
    phase:   this.warpPhase,
  },

  });
    this.pos = new Vector3(x0, y0, z0);
    this.mesh.position.set(x0, y0, z0);

    this.meshDisk.position.set(x0, y0, z0);
    this.innerMesh.position.set(x0, y0, z0);

    this.meshDisk.visible = sceneManager.commands.controls.spectrum;
    this.mesh.visible = sceneManager.commands.controls.spectrum;

    this.applyGrav(2);

    updateMagneticLines(this.diskLines, this.oscMag);
    updateColumnLines(this.upLines, this.oscMag);
    updateColumnLines(this.downLines, this.oscMag);

    updateJets(this.jets)
    this.updateParts(speed)
  }
  applyRotateDisk() {
    var angle = Math.atan2(
      -sceneManager.camera.position.z + this.mesh.position.z,
      -sceneManager.camera.position.x + this.mesh.position.x
    );
    angle = MathUtils.radToDeg(angle);
    this.materialDisk.uniforms.angle.value = angle;

    this.material.uniforms.uBeamingAngle.value = angle;

    this.material.uniforms.uBeamingFactor.value =
      0.82 * this.calculateBeaming(3e-2);
  }
}
